import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Typography, Stack, Link, styled, Box } from '@mui/material';
import { CheckmarkCircleFilled, Document } from '~/assets/icons';
import { theme } from '~/shared/styles/theme';
import { THEME_COLORS } from 'common';
import { ROUTES, SENTRY_OPERATIONS } from '~/shared/constants';
import {
  ConsentCheckbox,
  PrimaryButton,
  OnboardingLayout,
} from '../components';
import { SUBMIT_FAMILY_TREE_PROFILE_CONSENT } from '~/graphql/mutations';
import { ME } from '~/graphql/queries';
import useToast from '~/shared/hooks/useToast';
import Toast from '~/shared/components/Toast';
import { reportError } from '~/shared/utils/sentry';
import AppHeader from '~/shared/components/AppHeader';
import { useAcceptedInvitationContext } from '~/modules/invitation/hooks/useAcceptedInvitationContext';

interface ConsentFormData {
  hasConsent: boolean;
}

export const FamilyTreeConsentScreen = () => {
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();

  const { isEditor } = useAcceptedInvitationContext();

  const { control, watch, handleSubmit } = useForm<ConsentFormData>({
    defaultValues: { hasConsent: false },
  });

  const [submitFamilyTreeProfileConsent, { loading: mutationLoading }] =
    useMutation(SUBMIT_FAMILY_TREE_PROFILE_CONSENT, {
      refetchQueries: [{ query: ME }],
      awaitRefetchQueries: true,
    });

  const isConsentChecked = watch('hasConsent');

  // Content based on user type
  const title = isEditor ? 'Join Your Family Tree' : 'Start Your Family Tree';
  const description = isEditor
    ? 'To join your family tree, we need your consent to include your profile.'
    : 'To build your family tree, we need your consent to include your profile.';
  const buttonText = isEditor ? 'Continue' : 'Create Family Tree';

  const ownerAgreementItems = [
    'Your name and profile will be visible in your family tree',
    'You can invite family members to join',
    "Your information will be shared on Quoiden's platform",
  ];

  const inviteeAgreementItems = [
    'Your name and profile will be visible in your family tree',
    "Your information will be shared on Quoiden's platform",
  ];

  const agreementItems = isEditor ? inviteeAgreementItems : ownerAgreementItems;

  const onSubmit = async () => {
    if (!isConsentChecked) {
      showToast('Please accept the consent to continue.', 'error');
      return;
    }

    try {
      await submitFamilyTreeProfileConsent();
      navigate(ROUTES.ONBOARDING.ROOT);
    } catch (error) {
      reportError(error, {
        operation: SENTRY_OPERATIONS.SUBMIT_FAMILY_TREE_PROFILE_CONSENT,
      });

      showToast('Unable to save your consent. Please try again.', 'error');
    }
  };

  return (
    <OnboardingLayout>
      <AppHeaderWrapper>
        <AppHeader />
      </AppHeaderWrapper>
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <ContentStack>
          <TitleTypography>{title}</TitleTypography>

          <DescriptionTypography>{description}</DescriptionTypography>

          <SectionHeading>What you're agreeing to:</SectionHeading>

          <AgreementList spacing={3}>
            {agreementItems.map((item, index) => (
              <AgreementItem key={index}>
                <CheckmarkIcon>
                  <CheckmarkCircleFilled />
                </CheckmarkIcon>
                <AgreementText>{item}</AgreementText>
              </AgreementItem>
            ))}
          </AgreementList>

          <ViewFullConsentLink
            // TODO: Add link to full consent form when available from Quoiden
            // Referencing Ticket #122
            href="#"
            onClick={(e) => e.preventDefault()}
            underline="always"
          >
            <DocumentIcon />
            View Full Consent Form
          </ViewFullConsentLink>

          <ConsentCheckbox
            name="hasConsent"
            control={control}
            label={
              <Typography
                fontSize="14px"
                lineHeight="20px"
                fontWeight={500}
                color={theme.palette.custom.mediumBrown}
              >
                I consent to having my name and profile entered into the family
                tree
              </Typography>
            }
          />
        </ContentStack>

        <ButtonStack>
          <PrimaryButton
            type="submit"
            variant="darkBrown"
            disabled={!isConsentChecked || mutationLoading}
          >
            {mutationLoading ? 'Continuing...' : buttonText}
          </PrimaryButton>
        </ButtonStack>
      </StyledForm>

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={hideToast}
      />
    </OnboardingLayout>
  );
};

const StyledForm = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  justifyContent: 'space-between',
});

const ContentStack = styled(Stack)({
  alignItems: 'center',
  gap: theme.spacing(2),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(12),
});

const TitleTypography = styled(Typography)({
  fontSize: '30px',
  fontWeight: 500,
  color: theme.palette.custom.darkBrown,
  textAlign: 'center',
});

const DescriptionTypography = styled(Typography)({
  fontSize: '16px',
  color: theme.palette.custom.mediumBrown,
  fontWeight: 600,
  textAlign: 'center',
  maxWidth: '600px',
});

const SectionHeading = styled(Typography)({
  fontSize: '18px',
  fontWeight: 700,
  color: theme.palette.custom.textSupporting,
  alignSelf: 'flex-start',
  marginTop: theme.spacing(8),
});

const AgreementList = styled(Stack)({
  width: '100%',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(4),
  marginLeft: theme.spacing(8),
  gap: theme.spacing(2),
});

const AgreementItem = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing(3),
});

const CheckmarkIcon = styled(Box)({
  display: 'flex',
  width: '32px',
  height: '32px',
  flexShrink: 0,
  '& svg': {
    width: '32px',
    height: '32px',
    '& circle': {
      fill: theme.palette.custom.limeGreen,
    },
    '& path': {
      stroke: THEME_COLORS.textMain,
    },
  },
});

const AgreementText = styled(Typography)({
  fontSize: '18px',
  fontWeight: 500,
  color: theme.palette.custom.textSupporting,
  lineHeight: '25px',
  flex: 1,
});

const ViewFullConsentLink = styled(Link)({
  color: theme.palette.custom.textSupporting,
  fontSize: '15px',
  fontWeight: 600,
  cursor: 'pointer',
  alignSelf: 'flex-start',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(6),
  marginLeft: theme.spacing(6),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  textDecorationColor: theme.palette.custom.textSupporting,
});

const DocumentIcon = styled(Document)({
  flexShrink: 0,
});

const ButtonStack = styled(Stack)({
  alignItems: 'center',
});

const AppHeaderWrapper = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1,
});
